import "./QnaResultsCard.scss";

import {
  AcceptIcon,
  Alert,
  Avatar,
  BanIcon,
  Button,
  EyeIcon,
  EyeSlashIcon,
  Flex,
  LikeIcon,
  Reaction,
  ReplyIcon,
  SendIcon,
  Text,
  Tooltip,
  UndoIcon,
} from "@fluentui/react-northstar";
import FormState from "@shopify/react-form-state";
import { SurveyLimits } from "@teams/cards";
import { QnaReplyScopeType, TeamsSurveyQnaResponse } from "@teams/types";
import { SurveyDTO } from "@teams/types/api";
import moment from "moment";
import React, { useCallback, useMemo, useState } from "react";

import { useUserInfo } from "../../../../../_shared/actions/useUserInfo";
import { TextAreaField } from "../../../../../_shared/components/fields/TextAreaField";
import {
  OverflowMenu,
  OverflowMenuItem,
} from "../../../../../_shared/components/Menus/OverflowMenu/OverflowMenu";
import { PollyIcon } from "../../../../../_shared/components/PollyIcon";
import { isError } from "../../../../../_shared/services/ChannelApi";
import { surveyApi } from "../../../../../_shared/services/SurveyApi";
import { useTeamsContext } from "../../../../../_shared/state/TeamsContextProvider";

type QnaResultsCardProps = {
  survey: SurveyDTO;
  response: TeamsSurveyQnaResponse;
  onChanged: () => void;
  compact?: boolean;
  flippedProps?: object;
};
export const QnaResultsCard: React.FC<QnaResultsCardProps> = ({
  survey,
  response,
  onChanged,
  compact,
  flippedProps,
}) => {
  const [showReplyBox, setShowReplyBox] = useState<boolean>(false);
  const { teamsContext } = useTeamsContext();
  const [isUpdating, setIsUpdating] = useState<boolean>();
  const [isEditingSubmission, setIsEditingSubmission] = useState<boolean>(false);
  const { user } = useUserInfo();

  const userReaction = useMemo(
    () => response.reactions.find(r => r.user.aadObjectId === user.userId),
    [response, user]
  );

  const isUserOwnResponse = useMemo(
    () => response.user.aadObjectId === user.userId,
    [response.user.aadObjectId, user.userId]
  );

  const sendUpdate = useCallback(
    async (apiCall: () => Promise<unknown>) => {
      setIsUpdating(true);
      try {
        await apiCall();
        onChanged();
      } catch (e) {
        console.error(e);
      }
      setIsUpdating(false);
    },
    [onChanged]
  );

  const smallGap = compact ? "gap.smaller" : "gap.small";
  const mediumGap = compact ? "gap.small" : "gap.medium";

  return (
    <Flex
      column
      className={`qna-result-card ${response.focused ? "qna-result-card__focus" : ""}`}
      gap={mediumGap}
      {...flippedProps}
    >
      <Flex column>
        <Flex gap={smallGap} vAlign="center">
          <Avatar name={response.user.name || "Anonymous"} size="smallest" />
          <Text content={response.user.name} size="small" />
          <Text
            content={`${moment.duration(moment().diff(response.createdAt)).humanize()} ago`}
            weight="light"
            size="smaller"
          />

          {!compact && (
            <Flex.Item push>
              <Flex>
                {isUserOwnResponse && !compact && (
                  <SubmissionManagementMenu
                    onEdit={() => {
                      setIsEditingSubmission(true);
                    }}
                  />
                )}

                <Flex>
                  {survey.allowReactions && response.state === "approved" && (
                    <Reaction
                      icon={
                        <LikeIcon
                          size="small"
                          style={{ color: userReaction ? "var(--polly-accent-color)" : undefined }}
                        />
                      }
                      onClick={() => {
                        if (survey.state !== "open") {
                          return;
                        }

                        sendUpdate(() =>
                          surveyApi.toggleEmojiReaction(
                            teamsContext,
                            survey.id!,
                            response.id,
                            "qna",
                            ":thumbsup:"
                          )
                        );
                      }}
                      content={
                        <Text
                          style={{ marginLeft: "4px" }}
                          color={userReaction ? "brand" : undefined}
                          content={
                            response.reactions?.filter(r => r.reaction === ":thumbsup:").length || 0
                          }
                        />
                      }
                    />
                  )}
                </Flex>
              </Flex>
            </Flex.Item>
          )}
        </Flex>
      </Flex>
      {isEditingSubmission ? (
        <FormState
          initialValues={{
            responseText: response.responses[0].value as string,
          }}
          onSubmit={async formDetails => {
            const { fields } = formDetails;

            const apiResponse = await surveyApi.updateQnaResponse(
              teamsContext,
              survey.id!,
              response.id,
              {
                responseValue: fields.responseText.value,
              }
            );

            if (isError(apiResponse)) {
              fields.responseText.error = "Unable to update this response";
            } else setIsEditingSubmission(false);
          }}
        >
          {({ fields, submit, submitting, errors }) => {
            return (
              <Flex column gap="gap.small">
                <TextAreaField
                  field={fields.responseText}
                  readOnly={submitting}
                  disabled={submitting}
                  maxLength={SurveyLimits.Questions.OpenEnded.Value.MaxLength}
                  rows={3}
                  resize="none"
                  autoFocus
                  fluid
                  onKeyPress={e => {
                    if (e.key === "Enter") {
                      submit();
                    }
                  }}
                />
                {errors.some(e => !!e?.message) && <Text content={errors[0]?.message} />}

                <Flex.Item push>
                  <Flex gap={smallGap}>
                    <Flex.Item size="size.half">
                      <Button content="Cancel" onClick={() => setIsEditingSubmission(false)} />
                    </Flex.Item>
                    <Flex.Item size="size.half">
                      <Button primary content="Update" onClick={submit} loading={submitting} />
                    </Flex.Item>
                  </Flex>
                </Flex.Item>
              </Flex>
            );
          }}
        </FormState>
      ) : (
        <>
          <Flex column gap="gap.small">
            <Text content={response.responses[0]?.value} />
            {response.replies?.map(reply => (
              <Flex
                key={`qna-result-card-reply-${reply.id}`}
                column
                className="qna-result-card__reply"
                gap={mediumGap}
              >
                <Flex gap={smallGap}>
                  <Avatar name={reply.user.name} size="smallest" />
                  <Text
                    content={`${reply.user.name} replied ${
                      reply.scope === "private" ? "privately" : ""
                    }`}
                    size="small"
                  />
                </Flex>
                <Text content={reply.value} />
              </Flex>
            ))}
          </Flex>

          {!compact && survey.state === "open" && survey.role === "own" && (
            <Flex column gap={mediumGap}>
              <Flex vAlign="center">
                {!showReplyBox && (response.state === "approved" || response.state === "pending") && (
                  <Tooltip
                    content={`Send a ${response.state === "pending" ? "private" : "public"} reply`}
                  >
                    <Button
                      content="Reply"
                      text
                      disabled={isUpdating}
                      size="small"
                      icon={<ReplyIcon />}
                      onClick={() => setShowReplyBox(true)}
                    />
                  </Tooltip>
                )}

                <Flex.Item push>
                  <Flex gap="gap.smaller">
                    {response.state === "approved" && (
                      <Tooltip
                        content={
                          response.focused
                            ? "Remove focus from this question"
                            : "Focus this question for all users"
                        }
                      >
                        <Button
                          iconOnly
                          icon={response.focused ? <EyeSlashIcon /> : <EyeIcon />}
                          onClick={() =>
                            sendUpdate(() =>
                              surveyApi.updateQnaResponse(teamsContext, survey.id!, response.id, {
                                focused: !response.focused,
                              })
                            )
                          }
                          disabled={isUpdating}
                        />
                      </Tooltip>
                    )}

                    {(response.state === "approved" || response.state === "pending") && (
                      <Tooltip content={response.state === "pending" ? "Dismiss" : "Mark Answered"}>
                        <Button
                          iconOnly
                          icon={response.state === "pending" ? <BanIcon /> : <AcceptIcon />}
                          onClick={() =>
                            sendUpdate(() =>
                              surveyApi.updateQnaResponse(teamsContext, survey.id!, response.id, {
                                state: response.state === "approved" ? "done" : "dismissed",
                              })
                            )
                          }
                          disabled={isUpdating}
                        />
                      </Tooltip>
                    )}

                    {response.state !== "approved" && (
                      <Tooltip content={response.state === "done" ? "Undo Answered" : "Approve"}>
                        <Button
                          iconOnly
                          primary={response.state !== "done"}
                          icon={response.state === "done" ? <UndoIcon /> : <AcceptIcon />}
                          disabled={isUpdating}
                          onClick={() =>
                            sendUpdate(() =>
                              surveyApi.updateQnaResponse(teamsContext, survey.id!, response.id, {
                                state: "approved",
                              })
                            )
                          }
                        />
                      </Tooltip>
                    )}
                  </Flex>
                </Flex.Item>
              </Flex>

              {showReplyBox && (
                <FormState
                  initialValues={{
                    reply: "",
                    scope: (response.state === "pending"
                      ? "private"
                      : "public") as QnaReplyScopeType,
                  }}
                  onSubmit={async formDetails => {
                    const { fields } = formDetails;
                    if (!fields.reply.value) {
                      return;
                    }

                    await sendUpdate(() =>
                      surveyApi.createQnaReply(teamsContext, survey.id!, response.id, {
                        scope: response.state === "approved" ? "public" : "private",
                        value: fields.reply.value,
                      })
                    );

                    setShowReplyBox(false);
                  }}
                >
                  {({ fields, submit, submitting, errors }) => (
                    <Flex column gap={mediumGap} className="qna-create-form" hAlign="stretch">
                      <TextAreaField
                        field={fields.reply}
                        placeholder={
                          response.state === "pending"
                            ? `Enter a private reply for ${response.user.name}`
                            : "Enter a public reply"
                        }
                        readOnly={submitting}
                        disabled={submitting}
                        maxLength={SurveyLimits.Questions.OpenEnded.Value.MaxLength}
                        rows={3}
                        resize="none"
                        autoFocus
                        fluid
                        onKeyPress={e => {
                          if (e.key === "Enter") {
                            submit();
                          }
                        }}
                      />

                      {errors?.length > 0 && errors.map(e => <Alert danger content={e.message} />)}
                      <Flex gap={smallGap}>
                        <Flex.Item size="size.half">
                          <Button content="Cancel" onClick={() => setShowReplyBox(false)} />
                        </Flex.Item>
                        <Flex.Item size="size.half">
                          <Button
                            content="Send"
                            icon={<SendIcon size="small" />}
                            iconPosition="after"
                            onClick={submit}
                            loading={submitting}
                            disabled={submitting || fields.reply.value === ""}
                          />
                        </Flex.Item>
                      </Flex>
                    </Flex>
                  )}
                </FormState>
              )}
            </Flex>
          )}
        </>
      )}
    </Flex>
  );
};

const SubmissionManagementMenu: React.FC<{
  onEdit: () => void;
}> = ({ onEdit }) => {
  const menuItems: OverflowMenuItem[] = [];

  menuItems.push({
    key: "edit-submission",
    content: "Edit Submission",
    icon: (
      <Tooltip content="Edit Submission">
        <span>
          <PollyIcon icon="edit-icon" inheritColor />
        </span>
      </Tooltip>
    ),
    onClick: onEdit,
  });

  return <OverflowMenu items={menuItems} />;
};
